import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import './MediaImg.css';

/** Fly cold-start: keep total retry window ~a few seconds without hammering too hard. */
const MAX_RETRIES = 4;
const RETRY_DELAYS_MS = [400, 800, 1600, 3200];
const DEFAULT_FALLBACK = '/img/stat_re_armes.png';

function isRetryable(url) {
    if (!url || typeof url !== 'string') return false;
    if (url.startsWith('/img/') || url.startsWith('data:') || url.startsWith('blob:')) return false;
    if (url.startsWith('/media/')) return true;
    try {
        const path = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://local').pathname;
        return path.startsWith('/media/');
    } catch {
        return false;
    }
}

function withRetryParam(url, attempt) {
    if (!url || attempt <= 0) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}_r=${attempt}`;
}

/**
 * Item/media image with shimmer while loading and backoff retries for Fly cold starts.
 * Pass className/style onto the inner <img> so existing icon CSS keeps matching.
 *
 * Important: cache hits often finish before React binds onLoad. We sync from
 * img.complete after paint so those don't stay stuck on the shimmer forever.
 * Broken-cache sync is deferred one frame — mobile WebKit can briefly report
 * complete + naturalWidth 0 while swapping src, which used to burn all retries.
 */
export default function MediaImg({
    src,
    alt = '',
    className = '',
    style,
    fallback = DEFAULT_FALLBACK,
    loading,
    retry = true,
    resolve = true,
    fill = false,
    ...rest
}) {
    const resolvedSrc = resolve ? resolveMediaUrl(src) : src;
    const srcKey = resolvedSrc || '';

    const [attempt, setAttempt] = useState(0);
    const [useFb, setUseFb] = useState(false);
    const [phase, setPhase] = useState(srcKey ? 'loading' : 'failed');
    const [seenKey, setSeenKey] = useState(srcKey);

    const imgRef = useRef(null);
    const timerRef = useRef(null);
    const syncRafRef = useRef(0);
    const srcGenRef = useRef(0);
    const displayGenRef = useRef(0);
    const attemptRef = useRef(0);
    const useFbRef = useRef(false);
    const resolvedRef = useRef(resolvedSrc);
    const retryRef = useRef(retry);
    const fallbackRef = useRef(fallback);

    // Keep retry identity in sync before paint when src changes (avoids a stale ?_r=N flash).
    if (srcKey !== seenKey) {
        setSeenKey(srcKey);
        setAttempt(0);
        setUseFb(false);
        setPhase(srcKey ? 'loading' : 'failed');
        srcGenRef.current += 1;
    }

    attemptRef.current = attempt;
    useFbRef.current = useFb;
    resolvedRef.current = resolvedSrc;
    retryRef.current = retry;
    fallbackRef.current = fallback;

    const displaySrc = useFb
        ? fallback
        : withRetryParam(resolvedSrc, attempt);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (syncRafRef.current) cancelAnimationFrame(syncRafRef.current);
    }, []);

    const markReady = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (syncRafRef.current) cancelAnimationFrame(syncRafRef.current);
        setPhase('ready');
    };

    const handleError = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (syncRafRef.current) cancelAnimationFrame(syncRafRef.current);

        const cur = resolvedRef.current;
        const n = attemptRef.current;
        const onFb = useFbRef.current;
        const fb = fallbackRef.current;

        if (!onFb && retryRef.current && isRetryable(cur) && n < MAX_RETRIES) {
            const delay = RETRY_DELAYS_MS[n] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
            const srcGen = srcGenRef.current;
            timerRef.current = setTimeout(() => {
                if (srcGenRef.current !== srcGen) return;
                setAttempt((a) => a + 1);
            }, delay);
            return;
        }

        if (!onFb && fb && fb !== cur) {
            setUseFb(true);
            setPhase('loading');
            return;
        }

        setPhase('failed');
    };

    useLayoutEffect(() => {
        displayGenRef.current += 1;
        const displayGen = displayGenRef.current;
        if (syncRafRef.current) cancelAnimationFrame(syncRafRef.current);

        const img = imgRef.current;
        if (!img || !img.complete) return;

        if (img.naturalWidth > 0) {
            markReady();
            return;
        }

        // Defer failure: engines can report complete+0 for one frame after a src swap.
        syncRafRef.current = requestAnimationFrame(() => {
            syncRafRef.current = 0;
            if (displayGenRef.current !== displayGen) return;
            const el = imgRef.current;
            if (!el || !el.complete) return;
            if (el.naturalWidth > 0) markReady();
            else handleError();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync against latest displaySrc only
    }, [displaySrc]);

    if (!displaySrc && phase === 'failed') return null;

    const wrapCls = [
        'media-img',
        fill ? 'media-img--fill' : '',
        phase === 'loading' ? 'media-img--loading' : '',
    ].filter(Boolean).join(' ');

    return (
        <span className={wrapCls} aria-hidden={alt === '' ? true : undefined}>
            <img
                ref={imgRef}
                className={className || undefined}
                style={style}
                src={displaySrc || fallback}
                alt={alt}
                loading={loading}
                decoding="async"
                {...rest}
                onLoad={markReady}
                onError={handleError}
            />
        </span>
    );
}
